const installations = new WeakMap()

const opensDialog = action => action.name === 'showActionDialog'
  || (action.name === '__dispatch' && action.params?.[0] === 'showActionDialog')

/** Acquire before Livewire buffers/squashes actions, including direct dispatches. */
export default function installActionTriggerGuard(Livewire, eventTarget = globalThis.document, pageTarget = globalThis.window) {
  if (!Livewire?.interceptAction) return null
  if (installations.has(Livewire)) return installations.get(Livewire)

  const openings = new Map()
  const reset = () => openings.clear()
  const restore = event => { if (event.persisted) reset() }
  const removeInterceptor = Livewire.interceptAction(({ action, onFinish }) => {
    if (!opensDialog(action) || action.isCancelled()) return

    const owner = openings.get(action.component)
    if (owner && owner !== action) {
      // Livewire's in-flight scheduler can still call fire() after cancel().
      // Only rejected duplicates must permanently refuse that deferred replay.
      action.fire = () => {}
      action.promise?.catch?.(() => {})
      action.cancel()
      return
    }

    openings.set(action.component, action)
    onFinish(() => {
      if (openings.get(action.component) === action) openings.delete(action.component)
    })
  })

  eventTarget?.addEventListener('livewire:navigating', reset)
  pageTarget?.addEventListener('pagehide', reset)
  pageTarget?.addEventListener('pageshow', restore)
  let disposed = false
  const dispose = () => {
    if (disposed) return
    disposed = true
    removeInterceptor?.()
    reset()
    eventTarget?.removeEventListener('livewire:navigating', reset)
    pageTarget?.removeEventListener('pagehide', reset)
    pageTarget?.removeEventListener('pageshow', restore)
    installations.delete(Livewire)
  }
  installations.set(Livewire, dispose)
  return dispose
}

/** Track only actions synchronously caused by this dispatch, never other requests. */
export function trackActionTrigger(Livewire, dispatch, finish) {
  installActionTriggerGuard(Livewire)
  const pending = new Set()
  let collecting = true
  const settle = () => {
    if (!collecting && pending.size === 0) finish()
  }
  const removeInterceptor = Livewire?.interceptAction?.(({ action, onFinish }) => {
    if (action.isCancelled()) return
    pending.add(action)
    onFinish(() => {
      pending.delete(action)
      settle()
    })
  })
  try {
    dispatch()
  } catch (error) {
    finish()
    throw error
  } finally {
    removeInterceptor?.()
    collecting = false
    // No listeners/no Livewire work: collapse a synchronous burst, then release.
    queueMicrotask(settle)
  }
}
